import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import {
  currentBuildMonth,
  useCreateAssemblyDevice,
  type DeviceAssemblyRow,
} from "@/hooks/use-device-assembly";

const SERIAL_RE = /^SSL\d{3,4}$/;
const PARTICLE_RE = /^P[A-Z0-9]{14}\s[A-Z0-9]{15}$/;
const PCB_RE = /^\d{6}\s?\d{4}$/;

interface Props {
  rows: DeviceAssemblyRow[];
}

export const AddDeviceCard = ({ rows }: Props) => {
  const thisMonth = currentBuildMonth();

  // All months (existing + current), sorted ascending (oldest -> newest).
  const monthSortKey = (mmYy: string) => {
    const [mm, yy] = mmYy.split("-");
    return Number(`20${yy}${mm.padStart(2, "0")}`);
  };
  const availableMonths = useMemo(() => {
    const set = new Set(rows.map((r) => r.build_month));
    set.add(thisMonth);
    return Array.from(set).sort((a, b) => monthSortKey(a) - monthSortKey(b));
  }, [rows, thisMonth]);

  const [buildMonth, setBuildMonth] = useState<string>(thisMonth);

  const batchesForMonth = useMemo(() => {
    const set = new Set(
      rows.filter((r) => r.build_month === buildMonth).map((r) => r.batch_id),
    );
    if (set.size === 0) set.add(1);
    return Array.from(set).sort((a, b) => a - b);
  }, [rows, buildMonth]);

  const maxBatchInMonth = batchesForMonth[batchesForMonth.length - 1] ?? 1;

  const [activeBatch, setActiveBatch] = useState<number>(() => {
    const cur = rows.filter((r) => r.build_month === thisMonth);
    return cur.length ? Math.max(...cur.map((r) => r.batch_id)) : 1;
  });

  // Keep batch valid when month changes.
  if (!batchesForMonth.includes(activeBatch) && activeBatch <= maxBatchInMonth - 1) {
    // no-op guard
  }

  const [serialSuffix, setSerialSuffix] = useState("");
  const [particle, setParticle] = useState("");
  const [pcbSuffix, setPcbSuffix] = useState("");

  // Inherit PCB prefix from the most recent row in this batch/month.
  const inheritedPcbPrefix = useMemo(() => {
    const batchRows = rows
      .filter((r) => r.build_month === buildMonth && r.batch_id === activeBatch)
      .sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    const latest = batchRows[0];
    if (!latest) return "";
    const first = latest.pcb_serial_number.replace(/\s+/g, " ").split(" ")[0];
    return /^\d{6}$/.test(first) ? first : "";
  }, [rows, buildMonth, activeBatch]);

  const [pcbPrefixOverride, setPcbPrefixOverride] = useState<string | null>(
    null,
  );
  const pcbPrefix = pcbPrefixOverride ?? inheritedPcbPrefix;

  const serial = `SSL${serialSuffix.trim()}`;
  const pcb = `${pcbPrefix} ${pcbSuffix.trim()}`.trim();

  const createMut = useCreateAssemblyDevice();

  const normalizePcb = (v: string) => {
    const compact = v.replace(/\s+/g, "");
    if (compact.length === 10 && /^\d{10}$/.test(compact)) {
      return `${compact.slice(0, 6)} ${compact.slice(6)}`;
    }
    return v.trim();
  };

  const serialValid = SERIAL_RE.test(serial.toUpperCase());
  const particleValid = PARTICLE_RE.test(particle.trim());
  const pcbValid = /^\d{6}$/.test(pcbPrefix) && /^\d{4}$/.test(pcbSuffix.trim());

  const canSubmit = serialValid && particleValid && pcbValid && !createMut.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      await createMut.mutateAsync({
        serialNumber: serial.toUpperCase(),
        particleSerialNumber: particle.trim(),
        pcbSerialNumber: normalizePcb(pcb),
        buildMonth,
        batchId: activeBatch,
      });
      toast.success("Device saved");
      setSerialSuffix("");
      setParticle("");
      setPcbSuffix("");
      // Keep pcbPrefix as-is (inherited from just-saved row).
      setPcbPrefixOverride(null);
    } catch (err: any) {
      toast.error(err?.message || "Failed to save device");
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Plus className="h-5 w-5" />
          Add New Device
        </CardTitle>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">Month</span>
            <Select
              value={buildMonth}
              onValueChange={(v) => {
                setBuildMonth(v);
                const monthBatches = rows
                  .filter((r) => r.build_month === v)
                  .map((r) => r.batch_id);
                setActiveBatch(monthBatches.length ? Math.max(...monthBatches) : 1);
                setPcbPrefixOverride(null);
              }}
            >
              <SelectTrigger className="h-8 w-[110px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {availableMonths.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">Batch</span>
            <Select
              value={String(activeBatch)}
              onValueChange={(v) => {
                setActiveBatch(Number(v));
                setPcbPrefixOverride(null);
              }}
            >
              <SelectTrigger className="h-8 w-[90px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {batchesForMonth.map((b) => (
                  <SelectItem key={b} value={String(b)}>
                    Batch {b}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setActiveBatch(maxBatchInMonth + 1);
              setPcbPrefixOverride("");
            }}
          >
            Start new batch
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          <div className="space-y-1">
            <Label htmlFor="serial-suffix">Serial Number</Label>
            <div className="flex gap-2">
              <Input
                value="SSL"
                readOnly
                tabIndex={-1}
                className="w-16 bg-muted text-muted-foreground"
              />
              <Input
                id="serial-suffix"
                placeholder="991"
                inputMode="numeric"
                maxLength={4}
                value={serialSuffix}
                onChange={(e) =>
                  setSerialSuffix(e.target.value.replace(/\D/g, ""))
                }
                autoComplete="off"
                className="flex-1"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Enter 3 or 4 digits after SSL
            </p>
            {serialSuffix && !serialValid && (
              <p className="text-xs text-destructive">Invalid serial format</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="particle">Particle Serial Number</Label>
            <Input
              id="particle"
              placeholder="P034AD2164FGP5D 37NAPDSTT46N2RS"
              value={particle}
              onChange={(e) => setParticle(e.target.value.toUpperCase())}
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">
              30 characters, format: P + 14 chars, space, 15 chars
            </p>
            {particle && !particleValid && (
              <p className="text-xs text-destructive">Invalid Particle serial format</p>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="pcb-suffix">Main PCB Serial Number</Label>
            <div className="flex gap-2 items-center">
              <Input
                id="pcb-prefix"
                placeholder="054681"
                inputMode="numeric"
                maxLength={6}
                value={pcbPrefix}
                onChange={(e) =>
                  setPcbPrefixOverride(e.target.value.replace(/\D/g, ""))
                }
                autoComplete="off"
                className="w-24"
              />
              <span className="text-muted-foreground">-</span>
              <Input
                id="pcb-suffix"
                placeholder="0198"
                inputMode="numeric"
                maxLength={4}
                value={pcbSuffix}
                onChange={(e) =>
                  setPcbSuffix(e.target.value.replace(/\D/g, ""))
                }
                autoComplete="off"
                className="flex-1"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Batch prefix (6 digits) auto-fills from the last device
            </p>
            {(pcbPrefix || pcbSuffix) && !pcbValid && (
              <p className="text-xs text-destructive">Invalid PCB format</p>
            )}
          </div>

          <div className="md:col-span-3 flex justify-end">
            <Button type="submit" disabled={!canSubmit}>
              {createMut.isPending && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Assign & Save
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};